// A vehicle's geofence visits over the last 30 days (unit page): each arrival paired with the
// departure after it, newest first. Worked out by ingest from the stored positions.
import { useEffect, useMemo, useState } from 'react';
import { geofences, VehicleGeofenceEvent } from '../api';
import { dateTime, dayLabel, duration, time } from '../format';
import { Swatch } from '../pages/Geofences';
import { Link } from './Links';

interface Visit {
  geofence: VehicleGeofenceEvent['geofence'];
  arrived: VehicleGeofenceEvent | null;   // null: inside since before the 30 days
  left: VehicleGeofenceEvent | null;      // null: still inside
}

const DAYS = 30;
const SHOWN = 20;
// Time, with the day small under it (left out when it's the arrival's day).
const When = ({ at, sameDayAs }: { at: string; sameDayAs?: string }) => (
  <>{time(at)}{(!sameDayAs || dayLabel(sameDayAs) !== dayLabel(at)) && <span className="loc-coords">{dayLabel(at)}</span>}</>
);

function visitsOf(events: VehicleGeofenceEvent[]): Visit[] {
  const open = new Map<number, Visit>();
  const out: Visit[] = [];
  for (const e of [...events].reverse()) {   // oldest first
    if (e.kind === 'enter') {
      const v = { geofence: e.geofence, arrived: e, left: null };
      open.set(e.geofence.id, v);
      out.push(v);
    } else {
      const v = open.get(e.geofence.id);
      if (v) { v.left = e; open.delete(e.geofence.id); } else out.push({ geofence: e.geofence, arrived: null, left: e });
    }
  }
  return out.reverse();
}

export function GeofenceVisits({ vehicleId }: { vehicleId: number }) {
  const [events, setEvents] = useState<VehicleGeofenceEvent[] | null>(null);
  const [error, setError] = useState('');
  const [all, setAll] = useState(false);
  useEffect(() => {
    const to = new Date();
    const from = new Date(to.getTime() - DAYS * 86_400_000);
    geofences.vehicleEvents(vehicleId, from.toISOString(), to.toISOString()).then((r) => setEvents(r.events)).catch((e) => setError(e.message));
  }, [vehicleId]);
  const visits = useMemo(() => visitsOf(events ?? []), [events]);
  const shown = all ? visits : visits.slice(0, SHOWN);

  return (
    <section className="card" aria-label="Geofence visits">
      <div className="page-head">
        <h2>Geofence visits</h2>
        <Link to="/geofences" className="small">All geofences</Link>
      </div>
      {error && <p className="error" role="alert">{error}</p>}
      {events && visits.length === 0 && <p className="muted">No geofence visits in the last {DAYS} days.</p>}
      {visits.length > 0 && (
        <table className="table">
          <thead><tr><th>Geofence</th><th>Arrived</th><th>Left</th><th>Inside</th></tr></thead>
          <tbody>{shown.map((v, i) => {
            const passed = v.arrived?.passedThrough ?? false;
            const mins = v.arrived && v.left ? (Date.parse(v.left.at) - Date.parse(v.arrived.at)) / 60_000 : null;
            return (
              <tr key={i}>
                <td><Link to={`/geofences/${v.geofence.id}`}><Swatch colour={v.geofence.colour} />{v.geofence.name}</Link>
                  {v.geofence.removed && <span className="muted"> (removed)</span>}</td>
                <td className="nowrap">{v.arrived ? <When at={v.arrived.at} /> : <span className="muted">before</span>}</td>
                <td className="nowrap">{passed ? <span className="muted">passed through</span> : v.left ? <When at={v.left.at} sameDayAs={v.arrived?.at} /> : <strong>still inside</strong>}</td>
                <td className="nowrap">{passed ? '–' : mins !== null ? duration(mins) : '–'}
                  {v.left?.lastOtherSideAt && v.left.at && Date.parse(v.left.at) - Date.parse(v.left.lastOtherSideAt) > 10 * 60_000 && (
                    <span className="muted" title={`Last seen inside ${dateTime(v.left.lastOtherSideAt)}; no positions in between`}> · gap</span>
                  )}</td>
              </tr>
            );
          })}</tbody>
        </table>
      )}
      {visits.length > SHOWN && <button type="button" className="link" onClick={() => setAll(!all)}>{all ? 'Show fewer' : `Show all ${visits.length}`}</button>}
    </section>
  );
}
